import { toByteArray } from 'base64-js';
import { camelCase, defaults, every, findKey, get, isArray, isBoolean, isString, some, upperFirst } from 'lodash-es';
import { JwtClaims } from 'oidc-client-ts';

import { services } from 'services/base';
import {
  AvailabilityRule,
  EnvConfig,
  EnvMode,
  Json,
  LogLevel,
  SelectedDarkMode,
  SidebarItem,
  WidgetSidebarItem,
} from 'types';

import { getConfigs, getFeatures } from './config';
import logger from './logger';

export const isWidgetSidebarItem = (item: SidebarItem): item is WidgetSidebarItem =>
  !!(item as WidgetSidebarItem).widget;

const deprecatedMethods = {};
export const deprecatedMethod =
  (method: Function, methodName: string, newMethodName: string) =>
  (...args: any[]) => {
    if (!deprecatedMethods[methodName] && process.env.NODE_ENV === 'development') {
      deprecatedMethods[methodName] = true;
      logger.warn(`Deprecated method: \`${methodName}\`, please use ${newMethodName} instead!`);
    }
    return method(...args);
  };

export const getMetaValue = (name) => document.querySelector(`meta[name="${name}"]`)?.getAttribute('value');

export const isUrl = (url) => {
  if (!url) {
    return false;
  }
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/') || url.startsWith('.');
};

const calcVars = (value: string, vars: object, defaultValue: string) => {
  const key = (value.match(/\$\{(.+?)}/) || [])[1];
  if (!key) {
    return false;
  }
  return get(vars, key, defaultValue ?? key);
};

export const isAvailable = (available?: AvailabilityRule) => {
  const authManager = services.appFrame.auth.getAuthManager();
  const vars = {
    configs: getConfigs(),
    right: authManager?.getRightsMap() || {},
    audience: authManager?.getAudiencesMap() || {},
    features: getFeatures(),
  };

  const isItemAvailable = (item: AvailabilityRule) => {
    if (isBoolean(item)) {
      return item;
    }

    if (isString(item)) {
      const trimmedItem = item.trim();
      return !!calcVars(trimmedItem.startsWith('${') ? trimmedItem : `\${right.${trimmedItem}}`, vars, '');
    }

    if (isArray(item)) {
      return every(item, isItemAvailable);
    }

    return false;
  };

  return some(isArray(available) ? available : [available], isItemAvailable);
};

export const convertExceptionToJson = (ex: unknown): Json => {
  const { message, cause, stack } = ex as Error;
  const causeObj: Json = cause ? { cause: JSON.parse(JSON.stringify(cause)) } : {};
  const stackObj: Json = stack ? { stack: JSON.parse(JSON.stringify(stack)) } : {};
  return {
    ...JSON.parse(JSON.stringify(ex)),
    message,
    ...causeObj,
    ...stackObj,
  };
};

export const upperCamelCase = (str: string) => upperFirst(camelCase(str));

export const convertDarkModeStrToSelectedDarkMode = (darkModeStr: string | null): SelectedDarkMode => {
  if (darkModeStr === 'true') {
    return true;
  }
  if (darkModeStr === 'false') {
    return false;
  }
  return 'auto';
};

export const resolveEnvConfig = (envConfig?: string | EnvConfig): EnvConfig => {
  const resolvedConfig = isString(envConfig) ? { title: envConfig } : { ...(envConfig || {}) };
  const modeFromConfig = resolvedConfig.mode?.toLowerCase();
  const mode = EnvMode[findKey(EnvMode, (mode) => mode.toLowerCase() === modeFromConfig) as string] || EnvMode.Prod;

  return defaults(resolvedConfig, {
    mode,
    title: envConfig ? mode : undefined,
    allowEnvIndicator: mode !== EnvMode.Prod,
  });
};

export const convertStrToLogLevel = (logLevelStr: string | null) => {
  if (!logLevelStr) {
    return null;
  }

  const sanitizedLogLevel = logLevelStr.toUpperCase().trim();
  return Object.values(LogLevel).find((level) => level === sanitizedLogLevel) || null;
};

export const decodeTokenPayload = (token?: string | null) => {
  if (!token) {
    return null;
  }

  const payloadBase64 = token.split('.')[1];
  try {
    const decoder = new window.TextDecoder('utf-8');
    const result: JwtClaims = JSON.parse(
      decoder.decode(toByteArray(payloadBase64 + '='.repeat((4 - (payloadBase64.length % 4)) % 4))),
    );
    return result;
  } catch (err: unknown) {
    logger.warn('Decode base64 token payload using TextDecoder failed, fallback to `atob`!', err);
    // fallback to `atob` if `TextDecoder` or `base64-js` fails
    try {
      const result: JwtClaims = JSON.parse(atob(payloadBase64));
      return result;
    } catch (errAtoB) {
      logger.error('Token payload decoding failed using `atob`!', errAtoB);
    }
    return null;
  }
};
